import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { lockScroll, unlockScroll } from './top-utils.js';
import { initBgFadeIn, initBackgroundFadeOut, initTextAnimation } from './top-hero.js';
import { initTopRecruitAnimation } from './top-recruit.js';
import { addLightDots } from './top-lights.js';

/* ====================================
# 初回表示チェック
==================================== */
export const checkFirstVisit = () => {
  // セッションストレージをチェック（ブラウザ閉じるまで記憶）
  const hasSeenAnimation = sessionStorage.getItem('hasSeenLoadingAnimation');

  if (hasSeenAnimation) {
    // 2回目以降は即座にメインコンテンツ表示
    showMainContentDirectly();
    return false;
  }

  // 初回なのでフラグを設定
  sessionStorage.setItem('hasSeenLoadingAnimation', 'true');
  return true;
};

/* ====================================
# 直接メインコンテンツ表示
==================================== */
export const showMainContentDirectly = () => {
  const logoLoading = document.querySelector('.js-logo-loading');
  const animationElement = document.querySelector('.js-lottie-animation');

  // 2回目以降はテキスト要素を最初から表示状態にする
  const heroText = document.querySelector('.p-top-hero__text');
  const mobileSubElement = document.querySelector('.p-top-hero__sub--sp');

  // ロゴローディング要素はそのまま非表示のまま（何もしない）

  // Lottieアニメーションを背景モードで表示
  if (animationElement) {
    animationElement.classList.add('is-background');
    // 背景用のLottieアニメーションを読み込み（静止画または短いループ）
    lottieInstance = initBackgroundLottie(animationElement);
  }

  // 背景要素を初期位置に設定してフェードインアニメーション開始
  initBgFadeIn();

  // メインコンテンツをすぐに表示
  document.querySelector('#top')?.classList.add('is-loaded');

  // 2回目以降でもScrollTriggerを初期化
  // 競合を避けるため、背景と採用セクションを同時に初期化
  setTimeout(() => {
    // 背景フェードアウトアニメーションを設定
    initBackgroundFadeOut();

    // 採用セクションのScrollTriggerを再作成
    initTopRecruitAnimation();

    // 全体のScrollTriggerをリフレッシュ
    if (typeof ScrollTrigger !== 'undefined') {
      ScrollTrigger.refresh();
    }
  }, 100);

  // 2度目以降でもテキスト→ハイライトアニメーションを実行（完了状態）
  setTimeout(() => {
    initTextAnimation(false); // 2回目以降なのでfalse
  }, 500); // 少し遅延させて実行
};

/* ====================================
# 背景用Lottieアニメーション
==================================== */
export const initBackgroundLottie = (container) => {
  // 既存のLottieインスタンスがある場合は破棄
  if (lottieInstance) {
    lottieInstance.destroy();
    lottieInstance = null;
  }

  // コンテナをクリア
  container.innerHTML = '';

  // 背景用のLottieアニメーション（ループ or 最後のフレームで停止）
  lottieInstance = lottie.loadAnimation({
    container: container,
    renderer: 'svg',
    loop: false,
    autoplay: false, // 自動再生しない
    path: window.innerWidth <= 768 ?
      ajax_object.theme_path + '/assets/animations/fv_animation_sp.json' :
      ajax_object.theme_path + '/assets/animations/fv_animation_pc.json'
  });

  // アニメーションが読み込まれたら最後のフレームに移動
  lottieInstance.addEventListener('data_ready', () => {
    lottieInstance.goToAndStop(lottieInstance.totalFrames - 1, true);

    // 手前の枠線に光の点を追加（重複防止済み）
    setTimeout(() => {
      addLightDots(container);
    }, 100); // SVGが完全に描画されるまで少し待機
  });

  // エラーハンドリング
  lottieInstance.addEventListener('data_failed', (e) => {
    // data_readyが実行されなかった場合のみ実行
    setTimeout(() => {
      addLightDots(container);
    }, 1000);
  });

  return lottieInstance;
};

/* ====================================
# ロゴローディングアニメーション
==================================== */
export const initLogoLoading = (initLottieAnimation) => {
  const logoLoading = document.querySelector('.js-logo-loading');

  if (!logoLoading) {
    // ロゴローディングがない場合は直接Lottieアニメーション開始
    initLottieAnimation();
    return;
  }

  // ロゴローディングを表示
  logoLoading.classList.add('is-active');

  // ロゴアニメーション完了と同時にLottieアニメーション開始
  setTimeout(() => {
    initLottieAnimation();
  }, 2000); // ロゴアニメーション時間
};

/* ====================================
# Lottieアニメーションの設定
==================================== */
let lottieInitialized = false;
let lottieInstance = null;

export const initLottieAnimation = () => {
  // 重複初期化防止
  if (lottieInitialized) {
    return;
  }
  lottieInitialized = true;

  // アニメーション要素が存在するかチェック
  const animationContainer = document.querySelector('.js-lottie-animation');

  if (!animationContainer) {
    return;
  }

  // 既存のLottieインスタンスがある場合は破棄
  if (lottieInstance) {
    lottieInstance.destroy();
    lottieInstance = null;
  }

  // コンテナをクリア
  animationContainer.innerHTML = '';

  // アニメーションを表示
  animationContainer.classList.add('is-active');

  // Lottieアニメーション初期化
  lottieInstance = lottie.loadAnimation({
    container: animationContainer, // アニメーションを表示するDOM要素
    renderer: 'svg', // レンダリング形式 (svg, canvas, html)
    loop: false, // ループしない（一回のみ再生）
    autoplay: true, // 自動再生するかどうか
    path: window.innerWidth <= 768 ?
      ajax_object.theme_path + '/assets/animations/fv_animation_sp.json' :
      ajax_object.theme_path + '/assets/animations/fv_animation_pc.json' // アニメーションJSONファイルのパス
  });

  // アニメーション完了フラグ
  let animationCompleted = false;

  // アニメーション完了処理
  const completeAnimation = () => {
    if (!animationCompleted) {
      animationCompleted = true;
      showMainContent();
    }
  };

  // アニメーションイベントリスナー
  lottieInstance.addEventListener('complete', () => {
    completeAnimation();
  });

  // 最大表示時間の設定（フォールバック）
  setTimeout(() => {
    completeAnimation();
  }, 4000); // 4秒で強制終了

  // メインコンテンツを表示する関数
  const showMainContent = () => {
    const logoLoading = document.querySelector('.js-logo-loading');
    const animationElement = document.querySelector('.js-lottie-animation');
    const mainContent = document.querySelector('#top');

    if (animationElement && mainContent) {
      // ロゴローディング画面を非表示
      if (logoLoading) {
        logoLoading.classList.add('is-hidden');
      }

      // Lottieアニメーションを背景モードに変更
      animationElement.classList.remove('is-active');
      animationElement.classList.add('is-background');

      // 背景要素を初期位置に設定してフェードインアニメーション開始
      initBgFadeIn();

      // メインコンテンツを表示（必要に応じて）
      if (mainContent.classList) {
        mainContent.classList.add('is-loaded');
      }

      // Lottieアニメーション完了後にScrollTriggerを初期化
      // 競合を避けるため、背景と採用セクションを同時に初期化
      setTimeout(() => {
        // 背景フェードアウトアニメーションを設定
        initBackgroundFadeOut();

        // 採用セクションのScrollTriggerを再作成
        initTopRecruitAnimation();

        // 全体のScrollTriggerをリフレッシュ
        if (typeof ScrollTrigger !== 'undefined') {
          ScrollTrigger.refresh();
        }
      }, 100);

      // 初回訪問時：Lottieアニメーション完了後すぐにテキスト→ハイライトアニメーションを開始
      setTimeout(() => {
        initTextAnimation(true); // 初回訪問時なのでtrue
      }, 200); // 少し遅延させて実行
    }
  };

  return lottieInstance;
};